
import { useCallback } from 'react';
import { Lesson } from '@/models/Lesson';
import { LevelConfig } from '@/models/LearnTypes';
import { MutationHooks } from '../useLevelProgression.types';
import { usePerfectLevel } from '@/contexts/PerfectLevelContext';

interface UseLevelCompletionProps {
  userId: string | undefined;
  levels: LevelConfig[];
  setLevels: React.Dispatch<React.SetStateAction<LevelConfig[]>>;
  currentLevelIndex: number;
  totalXpGained: number;
  setTotalXpGained: React.Dispatch<React.SetStateAction<number>>;
  selectedLesson: Lesson | null;
  setShowLevelComplete: React.Dispatch<React.SetStateAction<boolean>>;
  setIsPerfectLevel: React.Dispatch<React.SetStateAction<boolean>>;
  levelCompletionCounts: Record<number, number>;
  setLevelCompletionCounts: React.Dispatch<React.SetStateAction<Record<number, number>>>;
  mutations: MutationHooks;
  practiceMode: boolean;
  handleImperfectLesson?: () => void;
}

export const useLevelCompletion = ({
  userId,
  levels,
  setLevels,
  currentLevelIndex,
  totalXpGained,
  setTotalXpGained,
  selectedLesson,
  setShowLevelComplete,
  setIsPerfectLevel,
  levelCompletionCounts,
  setLevelCompletionCounts,
  mutations,
  practiceMode,
  handleImperfectLesson
}: UseLevelCompletionProps) => {
  // Access the perfect level context to ensure accurate perfect status tracking
  const { getLevelPerfectStatus } = usePerfectLevel();
  
  /**
   * Clean up any recycled word tracking in localStorage
   */
  const cleanupRecycledWordTracking = useCallback(() => {
    try {
      // Get all localStorage keys
      const keys = Object.keys(localStorage);
      const recycledWordKeys = keys.filter(key => key.startsWith('recycled_word_'));
      
      if (recycledWordKeys.length > 0) {
        console.log(`Cleaning up ${recycledWordKeys.length} recycled word counters on level completion`);
        recycledWordKeys.forEach(key => {
          localStorage.removeItem(key);
        });
      }
    } catch (e) {
      console.error('Error cleaning up recycled word tracking:', e);
    }
  }, []);
  
  /**
   * Handle level completion logic
   */
  const completeLevelHandler = useCallback((wordsToReview: string[] = []) => {
    if (!levels[currentLevelIndex] || !selectedLesson) return;
    
    const currentLevel = levels[currentLevelIndex];
    
    // Force cleanup of any recycled word tracking to prevent infinite loops
    cleanupRecycledWordTracking();
    
    // ✨ ENHANCED PERFECT STATUS DETECTION ✨
    // Use our context to get the authoritative perfect status for this level
    const contextPerfectStatus = getLevelPerfectStatus(currentLevelIndex);
    
    // Also check legacy flags for backward compatibility
    const hadRecycledWords = (window as any).__HAD_RECYCLED_WORDS__ === true;
    const hadSpellingMistake = (window as any).__SPELLING_EXERCISE_HAD_MISTAKE__ === true;
    const matchExerciseHadMistake = (window as any).__MATCH_EXERCISE_HAD_MISTAKE__ === true;
    const flashCardExerciseHadMistake = (window as any).__FLASH_CARD_EXERCISE_HAD_MISTAKE__ === true;
    const manualPerfectStatus = (window as any).__PERFECT_LEVEL_STATUS__ === false ? false : null; // Explicit false takes precedence
    
    // More extensive check for ANY global status that indicates imperfect level
    // Including exercise-specific flags that might be set
    const anyGlobalFlagIndicatesImperfect = hadRecycledWords || 
                                             hadSpellingMistake || 
                                             matchExerciseHadMistake || 
                                             flashCardExerciseHadMistake || 
                                             manualPerfectStatus === false;
    
    // Check level-specific localStorage flags if available - now more comprehensive
    let localStoragePerfectStatus = null;
    try {
      // Check both global and level-specific flags in localStorage
      const spellingMistakeKey = `spelling_exercise_had_mistake_level_${currentLevelIndex}`;
      const matchMistakeKey = `match_exercise_had_mistake_level_${currentLevelIndex}`;
      const flashCardMistakeKey = `flash_card_exercise_had_mistake_level_${currentLevelIndex}`;
      const levelPerfectKey = `level_${currentLevelIndex}_perfect`;
      
      // If ANY localStorage flag indicates imperfect, the level is imperfect
      if (localStorage.getItem(spellingMistakeKey) === 'true' || 
          localStorage.getItem(matchMistakeKey) === 'true' ||
          localStorage.getItem(flashCardMistakeKey) === 'true' ||
          localStorage.getItem('spelling_exercise_had_mistake') === 'true' ||
          localStorage.getItem('match_exercise_had_mistake') === 'true' ||
          localStorage.getItem('flash_card_exercise_had_mistake') === 'true' ||
          localStorage.getItem('perfect_level_status') === 'false' ||
          localStorage.getItem('had_recycled_words') === 'true' ||
          localStorage.getItem(levelPerfectKey) === 'false') {
        localStoragePerfectStatus = false;
      }
    } catch (e) {
      console.error('Error checking localStorage for perfect status', e);
    }
    
    // ENHANCED FIX: Check explicitly for spelling exercise issues first
    // This creates a special defense against spelling exercises incorrectly showing as perfect
    let spellingSpecificImperfect = false;
    try {
      // Get level-specific mistake flags for spelling
      const levelSpecificMistake = localStorage.getItem(`spelling_exercise_had_mistake_level_${currentLevelIndex}`) === 'true';
      
      // Check if this is a spelling exercise by type
      const isSpellingExerciseType = currentLevel.type === 'spelling';
      
      // For spelling exercises, be extra cautious with perfect status
      if (isSpellingExerciseType) {
        console.log(`🔍 [completeLevelHandler] Spelling exercise detected, performing exhaustive perfect check`);
        
        // Check for ANY spelling mistake indicator anywhere in the system
        const anySpellingIndicator = 
          levelSpecificMistake || 
          hadSpellingMistake || 
          localStorage.getItem('spelling_exercise_had_mistake') === 'true';
          
        if (anySpellingIndicator) {
          spellingSpecificImperfect = true;
          console.log(`⚠️ [completeLevelHandler] Spelling exercise has mistake indicators, marking as imperfect`);
          
          // Sync the global state - to ensure everything is consistent
          (window as any).__SPELLING_EXERCISE_HAD_MISTAKE__ = true;
          (window as any).__PERFECT_LEVEL_STATUS__ = false;
          localStorage.setItem('spelling_exercise_had_mistake', 'true');
          localStorage.setItem('perfect_level_status', 'false');
        }
      }
    } catch (e) {
      console.error('Error in spelling-specific perfect status check', e);
    }
    
    // CRITICAL FIX: Check all sources for any indication of imperfection
    // If ANY system indicates this level was imperfect, trust that over everything else
    let finalPerfectStatus = true; // Start with assumption of perfection
    
    // Then check each source - if ANY indicates imperfect, the level is imperfect
    if (contextPerfectStatus === false || // Context explicitly says imperfect
        anyGlobalFlagIndicatesImperfect || // Any global flag indicates imperfect
        localStoragePerfectStatus === false || // Any localStorage flag indicates imperfect
        spellingSpecificImperfect) { // Spelling-specific imperfection logic
      finalPerfectStatus = false;
    }
    
    // If context has a definitive opinion, it's ONLY trusted if no other source indicates imperfection
    if (contextPerfectStatus !== null && contextPerfectStatus !== undefined) {
      if (contextPerfectStatus === true && (anyGlobalFlagIndicatesImperfect || localStoragePerfectStatus === false || spellingSpecificImperfect)) {
        // Context says perfect but other systems disagree - trust the imperfect signals
        finalPerfectStatus = false;
        console.warn('⚠️ [completeLevelHandler] Context reports perfect but other systems disagree - marking as imperfect');
      } else if (!anyGlobalFlagIndicatesImperfect && localStoragePerfectStatus !== false && !spellingSpecificImperfect) {
        // Only trust context when no other systems contradict it
        finalPerfectStatus = contextPerfectStatus;
      }
    }
    
    console.log(`🏆 [completeLevelHandler] Level ${currentLevelIndex} perfect status determination: ${finalPerfectStatus}`);
    console.log(`🔍 [completeLevelHandler] Sources: context=${contextPerfectStatus}, anyGlobalFlag=${anyGlobalFlagIndicatesImperfect}, localStorage=${localStoragePerfectStatus}, spellingSpecific=${spellingSpecificImperfect}`);
    console.log(`🔍 [completeLevelHandler] Details: hadRecycledWords=${hadRecycledWords}, hadSpellingMistake=${hadSpellingMistake}`);
    
    // ADDITIONAL SAFETY CHECK - Final verification specifically for spelling exercises
    try {
      // Double check one more time for ANY spelling mistake indicator
      if (currentLevel.type === 'spelling') {
        const anySpellingMistakeIndicator = 
          localStorage.getItem(`spelling_exercise_had_mistake_level_${currentLevelIndex}`) === 'true' ||
          localStorage.getItem('spelling_exercise_had_mistake') === 'true' ||
          (window as any).__SPELLING_EXERCISE_HAD_MISTAKE__ === true;
          
        if (anySpellingMistakeIndicator && finalPerfectStatus) {
          console.error(`🔴 [CRITICAL] Final safety check: Spelling mistake detected but finalPerfectStatus was true - forcing correction to false!`);
          finalPerfectStatus = false;
        }
      }
    } catch (e) {
      // Ignore errors in this final check
    }
    
    // Mark the level as completed
    setLevels(prevLevels => {
      const updatedLevels = [...prevLevels];
      updatedLevels[currentLevelIndex].state = 'completed';
      return updatedLevels;
    });
    
    // Track level completion count for XP calculation
    setLevelCompletionCounts(prev => {
      const newCount = (prev[currentLevelIndex] || 0) + 1;
      console.log(`Level ${currentLevelIndex + 1} completion count: ${newCount}`);
      return {
        ...prev,
        [currentLevelIndex]: newCount
      };
    });
    
    // When in practice mode, we don't award XP
    if (!practiceMode) {
      // Award XP for level completion
      // Reduce XP for repeated completions
      const levelCompletionCount = levelCompletionCounts[currentLevelIndex] || 0;
      const originalXP = currentLevel.xpReward || 0;
      // Reduce XP for repeated completions, but always give at least 1 XP
      const adjustedXP = Math.max(1, Math.ceil(originalXP / (levelCompletionCount + 1)));
      
      // Calculate perfect level bonus (always 5 XP if perfect)
      const perfectBonus = finalPerfectStatus ? 5 : 0;
      // Total XP is adjusted base XP plus perfect bonus
      const totalXP = adjustedXP + perfectBonus;
      
      setTotalXpGained(prev => prev + totalXP);
      
      console.log(`Level complete! Base XP: ${adjustedXP}, Perfect bonus: ${perfectBonus}, Total: ${totalXP} (completions: ${levelCompletionCount + 1})`);
      
      // Debug log to track why activity isn't being logged
      console.log(`📝 [completeLevelHandler] Activity log check - userId: ${userId ? 'exists' : 'missing'}, levelId: ${currentLevel.id ? currentLevel.id : 'missing'}, level index: ${currentLevelIndex}`);
      
      // Generate a fallback ID for levels without IDs
      const levelId = currentLevel.id || `generated-level-${currentLevelIndex}-${Date.now()}`;
      
      // Log activity in database - with safety check
      if (userId) {
        console.log(`📝 [completeLevelHandler] Recording level completion activity with baseXP=${adjustedXP}, perfectBonus=${perfectBonus}, total=${totalXP}`);
        mutations.activityMutation.mutate({
          userId,
          type: 'level-completed',
          lessonId: selectedLesson.id,
          lessonName: selectedLesson.name,
          levelId: levelId,
          levelIndex: currentLevelIndex,
          levelType: currentLevel.type,
          xpEarned: totalXP,
          baseXpEarned: adjustedXP,
          perfectLevelBonus: perfectBonus,
          isPerfect: finalPerfectStatus,
          adjustmentFactor: levelCompletionCount + 1
        });
      }
    } else {
      console.log('Practice mode - no XP awarded');
    }
    
    // If recycled words were used, mark the lesson as imperfect
    if (hadRecycledWords && handleImperfectLesson) {
      console.debug('⚠️ [completeLevelHandler] Level had recycled words - marking lesson as imperfect');
      handleImperfectLesson();
    }
    
    // Update the perfect level state
    setIsPerfectLevel(finalPerfectStatus);
    
    // Show level complete UI
    setShowLevelComplete(true);
  }, [
    levels,
    currentLevelIndex,
    setLevels,
    setShowLevelComplete,
    selectedLesson,
    setTotalXpGained,
    userId,
    mutations.activityMutation,
    levelCompletionCounts,
    setLevelCompletionCounts,
    practiceMode,
    setIsPerfectLevel,
    handleImperfectLesson,
    cleanupRecycledWordTracking,
    getLevelPerfectStatus
  ]);
  
  return {
    completeLevelHandler,
    cleanupRecycledWordTracking
  };
};
